import React, { useState } from "react";
import { useShow, useCustomMutation, useApiUrl } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Typography, Tag, Table, Card, Space, Button, Modal, Form, Select, Input, message, Badge } from "antd";
import {
  FileTextOutlined,
  SendOutlined,
  CheckOutlined,
  CloseOutlined,
  SwapOutlined,
  SyncOutlined,
  PrinterOutlined,
  EditOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";
import axios from "axios";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;
const { TextArea } = Input;

interface ActionButton {
  label: string;
  onClick: () => void;
  type?: "primary" | "default" | "dashed" | "text" | "link";
  icon?: React.ReactNode;
  danger?: boolean;
  ghost?: boolean;
  key?: string;
}

export const QuotationShow: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [form] = Form.useForm();

  const { queryResult } = useShow({
    resource: "quotations",
    meta: {
      fields: [
        "id",
        "quotationNumber",
        "customerTPIN",
        "customerId",
        "customerName",
        "currencyTypeCode",
        "exchangeRate",
        "subTotal",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "status",
        "quotationDate",
        "validUntil",
        "notes",
        "terms",
        "documentUrl",
        "createdAt",
        "updatedAt",
        {
          items: [
            "id",
            "inventoryItemId",
            "itemCode",
            "itemName",
            "itemClassCode",
            "packageUnitCode",
            "quantity",
            "quantityUnitCode",
            "unitPrice",
            "lineAmount",
            "taxRate",
            "taxAmount",
            "discountRate",
            "discountAmount",
            "totalAmount"
          ]
        },
        {
          billableServices: [
            "id",
            "description",
            "price",
            "taxId",
            "taxRate",
            "taxType",
            "customerName",
            "customerContact"
          ]
        }
      ]
    }
  });
  const { data, isLoading } = queryResult;
  const record = data?.data;
  const apiUrl = useApiUrl();

  const { mutate: send } = useCustomMutation();
  const { mutate: accept } = useCustomMutation();
  const { mutate: reject } = useCustomMutation();
  const { mutate: convert } = useCustomMutation();

  const handleSend = () => {
    send({
      url: `quotations/${record?.id}/send`,
      method: "post",
      successNotification: () => ({
        message: "Success",
        description: "Quotation sent successfully",
        type: "success",
      }),
    }, {
      onSuccess: () => {
        queryResult.refetch();
      }
    });
  };

  const handleAccept = () => {
    accept({
      url: `quotations/${record?.id}/accept`,
      method: "post",
      successNotification: () => ({
        message: "Success",
        description: "Quotation accepted successfully",
        type: "success",
      }),
    }, {
      onSuccess: () => {
        queryResult.refetch();
      }
    });
  };

  const handleReject = () => {
    reject({
      url: `quotations/${record?.id}/reject`,
      method: "post",
      successNotification: () => ({
        message: "Success",
        description: "Quotation rejected successfully",
        type: "success",
      }),
    }, {
      onSuccess: () => {
        queryResult.refetch();
      }
    });
  };

  const handleConvert = () => {
    convert({
      url: `quotations/${record?.id}/convert`,
      method: "post",
      successNotification: () => ({
        message: "Success",
        description: "Quotation converted to sale successfully",
        type: "success",
      }),
    }, {
      onSuccess: () => {
        queryResult.refetch();
      }
    });
  };

  const handleViewPdf = async () => {
    try {
      const response = await axios.get(`${apiUrl}/quotations/${record?.id}/pdf`);
      const documentUrl = response?.data?.documentUrl;

      if (documentUrl) {
        queryResult.refetch();
        window.open(documentUrl, '_blank');
      } else {
        message.error("Failed to get document URL");
      }
    } catch (error: any) {
      console.error("Error fetching PDF:", error);
      message.error(error?.response?.data?.message || "Failed to generate or retrieve PDF");
    }
  };

  const handleRegenerateSubmit = async (values: any) => {
    if (!record) return;

    setRegenerating(true);
    try {
      const request: RegenerationRequest = {
        regenerationType: values.regenerationType,
        reason: values.reason,
        preserveOriginal: true,
        notifyCustomer: false,
      };

      const result = await invoiceRegenerationService.regenerateQuotation(record.id, request);

      if (result.success) {
        message.success(`Quotation ${record.quotationNumber} regenerated successfully!`);
        setRegenerateModalVisible(false);
        queryResult.refetch();
      } else {
        message.error(`Failed to regenerate quotation: ${result.errorMessage}`);
      }
    } catch (error: any) {
      console.error("Quotation regeneration error:", error);
      message.error(`Failed to regenerate quotation: ${error.response?.data?.message || error.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleModalCancel = () => {
    setRegenerateModalVisible(false);
    form.resetFields();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Sent":
        return "blue";
      case "Draft":
        return "default";
      case "Accepted":
        return "green";
      case "Rejected":
        return "red";
      case "Expired":
        return "orange";
      case "Converted":
        return "purple";
      default:
        return "default";
    }
  };

  const displayCustomerTPIN = (tpin: string) => {
    if (!tpin || tpin === "0") {
      return <Text type="secondary" italic>Manual Entry</Text>;
    }
    return tpin;
  };

  const isExpired = () => {
    if (!record?.validUntil) return false;
    return new Date(record.validUntil) < new Date();
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "0.00";
    return value.toFixed(2);
  };

  const itemColumns = [
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      render: (text: string, record: any) => {
        const inventoryItemId = record?.inventoryItemId;
        return inventoryItemId ? (
          <Link to={`/products/show/${inventoryItemId}`} style={{ color: 'var(--color-primary)' }}>
            {text}
          </Link>
        ) : text;
      }
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (text: string, record: any) => {
        const inventoryItemId = record?.inventoryItemId;
        return inventoryItemId ? (
          <Link to={`/products/show/${inventoryItemId}`} style={{ color: 'var(--color-primary)' }}>
            {text}
          </Link>
        ) : text;
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value: number) => value?.toFixed(2)
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (value: number, record: any) => {
        const totalAmount = Number(record?.totalAmount) || 0;
        const quantity = Number(record?.quantity) || 1;
        const taxInclusiveUnitPrice = totalAmount / quantity;
        return `$${taxInclusiveUnitPrice.toFixed(2)}`;
      }
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'taxRate',
      render: (value: number) => `${value?.toFixed(2)}%`
    },
    {
      title: 'Tax Amount',
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      render: (value: number) => `$${value?.toFixed(2)}`
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (value: number) => `$${value?.toFixed(2)}`
    }
  ];

  const billableServicesColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (value: number) => `$${value?.toFixed(2)}`
    },
    {
      title: 'Tax Rate',
      dataIndex: 'taxRate',
      key: 'taxRate',
      render: (value: number) => `${value?.toFixed(2)}%`
    },
    {
      title: 'Tax Amount',
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      render: (value: any, record: any) => {
        const price = Number(record.price) || 0;
        const taxRate = (Number(record.taxRate) || 0) / 100;
        const taxAmount = price - (price / (1 + taxRate));
        return `$${taxAmount.toFixed(2)}`;
      }
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (value: any, record: any) => {
        const price = Number(record.price) || 0;
        return `$${price.toFixed(2)}`;
      }
    }
  ];

  // Build actions
  const actions: ActionButton[] = [
    {
      label: "Print",
      onClick: handleViewPdf,
      type: "default",
      icon: <PrinterOutlined />,
      key: "print",
    },
  ];

  if (record?.status === "Draft") {
    actions.push({
      label: "Edit",
      onClick: () => navigate(`/quotations/edit/${id}`),
      type: "default",
      icon: <EditOutlined />,
      key: "edit",
    });
    actions.push({
      label: "Send",
      onClick: handleSend,
      type: "primary",
      icon: <SendOutlined />,
      key: "send",
    });
  }

  if (record?.status === "Sent") {
    actions.push({
      label: "Accept",
      onClick: handleAccept,
      type: "primary",
      icon: <CheckOutlined />,
      key: "accept",
    });
  }

  if (record?.status === "Accepted" && !isExpired()) {
    actions.push({
      label: "Convert to Sale",
      onClick: handleConvert,
      type: "primary",
      icon: <SwapOutlined />,
      key: "convert",
    });
  }

  // Build more menu items
  const moreMenuItems: ActionButton[] = [];

  if (record?.documentUrl) {
    moreMenuItems.push({
      key: "regenerate",
      label: "Regenerate",
      onClick: () => setRegenerateModalVisible(true),
      icon: <SyncOutlined />,
    });
  }

  if (record?.status === "Sent") {
    moreMenuItems.push({
      key: "reject",
      label: "Reject",
      onClick: handleReject,
      icon: <CloseOutlined />,
      danger: true,
    });
  }

  if (isLoading) {
    return <div className="page-container">Loading...</div>;
  }

  return (
    <div className="page-container">
      <PageHeader
        title={`Quotation #${record?.quotationNumber || id}`}
        icon={<FileTextOutlined />}
        breadcrumbs={[
          { title: "Quotations", href: "/quotations" },
          { title: `#${record?.quotationNumber || id}` },
        ]}
        actions={actions}
        moreMenuItems={moreMenuItems.length > 0 ? moreMenuItems : undefined}
      >
        <div className="mini-stat-cards-row">
          <div className="mini-stat-card">
            <div className="mini-stat-label">Total Amount</div>
            <div className="mini-stat-value">${formatCurrency(record?.totalAmount)}</div>
          </div>
          <div className="mini-stat-card">
            <div className="mini-stat-label">Status</div>
            <div className={`status-badge ${record?.status === "Accepted" || record?.status === "Converted" ? "good" : record?.status === "Draft" || record?.status === "Sent" ? "pending" : "inactive"}`}>
              {(record?.status === "Accepted" || record?.status === "Converted") && <CheckCircleOutlined />}
              {(record?.status === "Draft" || record?.status === "Sent") && <ClockCircleOutlined />}
              {(record?.status === "Rejected" || record?.status === "Expired") && <CloseOutlined />}
              {record?.status}
              {isExpired() && record?.status !== "Converted" && record?.status !== "Rejected" && " (Expired)"}
            </div>
          </div>
          <div className="mini-stat-card">
            <div className="mini-stat-label">Valid Until</div>
            <div className="mini-stat-value" style={{ color: isExpired() ? 'var(--color-danger)' : 'inherit' }}>
              <DateField value={record?.validUntil} format="YYYY-MM-DD" />
            </div>
          </div>
        </div>
      </PageHeader>

      {/* Quotation Details */}
      <div className="info-cards-grid">
        <div className="info-card">
          <div className="info-card-header">Quotation Details</div>
          <div className="info-card-content">
            <div className="info-item">
              <span className="info-label">Quotation Number:</span>
              <span className="info-value">{record?.quotationNumber || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">
                <Tag color={getStatusColor(record?.status)}>{record?.status}</Tag>
                {isExpired() && record?.status !== "Converted" && record?.status !== "Rejected" && (
                  <Tag color="orange">EXPIRED</Tag>
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Quotation Date:</span>
              <span className="info-value"><DateField value={record?.quotationDate} format="YYYY-MM-DD" /></span>
            </div>
            <div className="info-item">
              <span className="info-label">Valid Until:</span>
              <span className="info-value" style={{ color: isExpired() ? 'var(--color-danger)' : 'inherit' }}>
                <DateField value={record?.validUntil} format="YYYY-MM-DD" />
              </span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-header">Customer Details</div>
          <div className="info-card-content">
            <div className="info-item">
              <span className="info-label">Customer Name:</span>
              <span className="info-value">
                {record?.customerId ? (
                  <Link to={`/customers/show/${record.customerId}`} style={{ color: 'var(--color-primary)' }}>
                    {record?.customerName}
                  </Link>
                ) : (
                  record?.customerName || '-'
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Customer TPIN:</span>
              <span className="info-value">{displayCustomerTPIN(record?.customerTPIN)}</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-header">Financial Summary</div>
          <div className="info-card-content">
            <div className="info-item">
              <span className="info-label">Subtotal:</span>
              <span className="info-value">${formatCurrency((record?.totalAmount || 0) - (record?.totalTax || 0))}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Tax:</span>
              <span className="info-value">${formatCurrency(record?.totalTax)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Amount:</span>
              <span className="info-value" style={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>
                {record?.currencyTypeCode} ${formatCurrency(record?.totalAmount)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Exchange Rate:</span>
              <span className="info-value">{record?.exchangeRate?.toFixed(4)}</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-header">System Information</div>
          <div className="info-card-content">
            <div className="info-item">
              <span className="info-label">Created At:</span>
              <span className="info-value"><DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm" /></span>
            </div>
            <div className="info-item">
              <span className="info-label">Updated At:</span>
              <span className="info-value"><DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm" /></span>
            </div>
            {record?.notes && (
              <div className="info-item">
                <span className="info-label">Notes:</span>
                <span className="info-value">{record?.notes}</span>
              </div>
            )}
            {record?.terms && (
              <div className="info-item">
                <span className="info-label">Terms:</span>
                <span className="info-value">{record?.terms}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quotation Items */}
      <div className="data-table-card">
        <Card
          title={
            <Space>
              <span>Quotation Items</span>
              <Badge count={record?.items?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
            </Space>
          }
        >
          {record?.items && record.items.length > 0 ? (
            <Table
              dataSource={record?.items}
              columns={itemColumns}
              rowKey={(record) => record.id}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Text type="secondary">No quotation items available</Text>
          )}
        </Card>
      </div>

      {/* Billable Services */}
      {record?.billableServices && record.billableServices.length > 0 && (
        <div className="data-table-card">
          <Card
            title={
              <Space>
                <span>Billable Services</span>
                <Badge count={record?.billableServices?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
              </Space>
            }
          >
            <Table
              dataSource={record?.billableServices}
              columns={billableServicesColumns}
              rowKey={(record) => record.id}
              pagination={false}
            />
          </Card>
        </div>
      )}

      {/* Regenerate Modal */}
      <Modal
        title="Regenerate Quotation"
        open={regenerateModalVisible}
        onCancel={handleModalCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegenerateSubmit}
        >
          <Form.Item
            name="regenerationType"
            label="Regeneration Type"
            rules={[{ required: true, message: 'Please select a regeneration type' }]}
          >
            <Select placeholder="Select regeneration type">
              <Select.Option value="BugFix">Bug Fix</Select.Option>
              <Select.Option value="DataCorrection">Data Correction</Select.Option>
              <Select.Option value="FormatUpdate">Format Update</Select.Option>
              <Select.Option value="CustomerRequest">Customer Request</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter reason for regeneration..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={regenerating}>
                Regenerate
              </Button>
              <Button onClick={handleModalCancel}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
